import type { BracketSchedulingConfig } from "../../Common/Brackets/types";
import { BracketType, BracketTypeEnum } from "../../Common/types";
import { equalPartsFor } from "./dropZoneItems";
import type { BracketSegment, RoundMarker } from "./dropZoneItems";

const ALL_SE_ROUNDS: RoundMarker[] = ["1/128", "1/64", "1/32", "1/16", "1/8", "quarter_final", "semi_final", "final"];

export interface BracketSlicingStrategy {
    getAvailableRounds(totalRealCompetitors: number): RoundMarker[];
    finalizeSegments(segments: BracketSegment[], config: BracketSchedulingConfig): BracketSegment[];
}

class SingleEliminationSlicingStrategy implements BracketSlicingStrategy {
    getAvailableRounds(totalRealCompetitors: number): RoundMarker[] {
        const bracketSize = nextPowerOfTwo(Math.max(2, totalRealCompetitors));
        const levels = Math.log2(bracketSize);

        return ALL_SE_ROUNDS.slice(-levels);
    }

    finalizeSegments(segments: BracketSegment[]): BracketSegment[] {
        return segments;
    }
}

class SE3rdPlacePlayoffSlicingStrategy implements BracketSlicingStrategy {
    private singleElimination = new SingleEliminationSlicingStrategy();

    getAvailableRounds(totalRealCompetitors: number): RoundMarker[] {
        const seRounds = this.singleElimination.getAvailableRounds(totalRealCompetitors);

        return [...seRounds.slice(0, -1), "third_place", "final"];
    }

    finalizeSegments(segments: BracketSegment[], config: BracketSchedulingConfig): BracketSegment[] {
        const isolatedSet = new Set(config.isolatedRounds);

        return isolatedSet.has("third_place") ? insertThirdPlaceBeforeFinal(segments, config) : absorbThirdPlaceIntoFinalSegment(segments);
    }
}

class UnsupportedBracketSlicingStrategy implements BracketSlicingStrategy {
    getAvailableRounds(): RoundMarker[] {
        return [];
    }

    finalizeSegments(segments: BracketSegment[]): BracketSegment[] {
        return segments;
    }
}

export function getBracketSlicingStrategy(bracketType: BracketType): BracketSlicingStrategy {
    switch (bracketType) {
        case BracketTypeEnum.SingleElimination:
            return new SingleEliminationSlicingStrategy();
        case BracketTypeEnum.SingleElimination3rdPlacePlayoff:
            return new SE3rdPlacePlayoffSlicingStrategy();
        default:
            return new UnsupportedBracketSlicingStrategy();
    }
}

function insertThirdPlaceBeforeFinal(segments: BracketSegment[], config: BracketSchedulingConfig): BracketSegment[] {
    const finalIndex = findFinalSegmentIndex(segments);
    const updatedSegments = [...segments];
    const insertIndex = segmentContainsFinalAsRest(segments[finalIndex]) ? finalIndex + 1 : finalIndex;

    updatedSegments.splice(insertIndex, 0, {
        from: "third_place",
        to: "third_place",
        equalParts: equalPartsFor(config, "third_place"),
        isRest: false,
        includesThirdPlace: false,
    });

    return updatedSegments;
}

function absorbThirdPlaceIntoFinalSegment(segments: BracketSegment[]): BracketSegment[] {
    const finalIndex = findFinalSegmentIndex(segments);

    return segments.map((segment, index) => {
        if (index !== finalIndex) {
            return segment;
        }

        return {
            ...segment,
            includesThirdPlace: true,
        };
    });
}

function findFinalSegmentIndex(segments: BracketSegment[]): number {
    const finalIndex = segments.findIndex(segment => segment.from === "final" || segment.to === "final");

    return finalIndex === -1 ? segments.length : finalIndex;
}

function segmentContainsFinalAsRest(segment: BracketSegment | undefined): boolean {
    return segment !== undefined && segment.isRest && (segment.from === "final" || segment.to === "final");
}

function nextPowerOfTwo(value: number): number {
    let size = 1;
    while (size < value) {
        size *= 2;
    }

    return size;
}
